"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import {
  CATALOG_COURSES,
  CATALOG_PRICE,
  catalogCourseFacts,
} from "@/lib/data/catalog-courses";
import type { CatalogModuleCounts } from "@/lib/data/catalog-module-counts";

/**
 * Explore Learning Paths (#courses): a card per course (lib/data/catalog-courses.ts, shared with
 * /pricing). The hero's and Find-your-path's "Browse All Courses" buttons land here.
 */
export default function CatalogCardsClient({ moduleCounts }: { moduleCounts: CatalogModuleCounts }) {
  return (
    <section id="courses" aria-labelledby="courses-heading" className="scroll-mt-20 py-size-xl bg-white">
      <div className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop">
        <Reveal>
          <h2 id="courses-heading" className="font-headline-lg text-headline-lg md:text-display-lg text-mkt-on-surface">
            Explore Learning Paths
          </h2>
        </Reveal>

        <ul className="list-none pl-0 ml-0 mt-size-lg grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-gutter">
          {CATALOG_COURSES.map(({ id, title, tagline, audience, level, focus, href, offerPlanKey }) => (
            <li key={id} id={`course-${id}`} className="scroll-mt-24">
              <Reveal className="h-full">
                <article
                  aria-labelledby={`course-${id}-title`}
                  className="relative h-full flex flex-col bg-white border border-mkt-outline-variant p-size-md rounded-xl shadow-[0_8px_28px_-4px_rgba(0,80,239,0.35)] hover:shadow-[0_10px_32px_-4px_rgba(0,80,239,0.5)] transition-shadow"
                >
                  <h3
                    id={`course-${id}-title`}
                    className="font-headline-lg text-headline-lg text-mkt-on-surface break-words [text-wrap:balance]"
                  >
                    {title}
                  </h3>
                  <p className="mt-size-xs font-bold text-mkt-primary text-body-lg">{tagline}</p>

                  <dl className="mt-size-md flex flex-col gap-size-xs font-body-md text-body-md">
                    <div className="flex gap-2">
                      <dt className="font-bold text-mkt-on-surface">For:</dt>
                      <dd className="text-mkt-on-surface-variant">{audience}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="font-bold text-mkt-on-surface">Level:</dt>
                      <dd className="text-mkt-on-surface-variant">{level}</dd>
                    </div>
                    <div>
                      <dt className="font-bold text-mkt-on-surface">Focus:</dt>
                      <dd className="mt-2">
                        <ul className="list-none pl-0 ml-0 flex flex-wrap gap-2">
                          {focus.map((topic) => (
                            <li
                              key={topic}
                              className="rounded-full border border-mkt-primary/20 bg-mkt-primary-container/[0.06] px-3 py-1 text-sm text-mkt-on-surface"
                            >
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-auto pt-size-md">
                    {/* The link's ::after stretches over the whole card, making the card the click target. */}
                    {/* Facts and price share a row; on a card too narrow for both, the button wraps under. */}
                    <div className="border-t border-mkt-outline-variant/30 pt-size-md flex flex-wrap items-center justify-between gap-size-sm">
                      <p className="whitespace-nowrap font-body-md text-body-md font-bold text-mkt-on-surface">
                        {catalogCourseFacts(moduleCounts[offerPlanKey])}
                      </p>
                      <Link
                        href={href}
                        aria-label={`${title}: ${CATALOG_PRICE}`}
                        className="group inline-flex shrink-0 after:absolute after:inset-0 after:rounded-xl after:content-[''] items-center gap-2 bg-mkt-primary-container text-mkt-on-primary font-headline-md text-headline-sm font-bold px-4 py-size-xs rounded-lg primary-cta-glow transition-all hover:scale-105 active:scale-95"
                      >
                        {CATALOG_PRICE}
                        <ArrowRight
                          aria-hidden="true"
                          className="h-[1em] w-[1em] transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </Link>
		      </div>
                    {/* <p className="mt-size-sm font-body-sm text-[15px] font-bold text-mkt-primary">{CATALOG_ACCESS_NOTE}</p> */}
                  </div>
                </article>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
